import { MatrixClient } from 'matrix-js-sdk/src';
import { MutableRefObject, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useToast from 'contexts/ToastProvider/useToast';
import logger from 'utils/logging/faro';
import { fetchMediaAsBlobUrl, uploadMediaToMatrix } from 'utils/matrix/matrixMediaUtils';
import { SetState } from 'utils/types';

import useMxCookies from './useMxCookies';

/**
 * Use avatar hook
 * @returns fetchAvatar - For fetching the avatar after updated or initializing the app
 * @returns changeAvatar - For changing the avatar
 * @returns clearAvatar - For clearing the avatar
 * @returns loading - The loading state when uploading or clearing the avatar
 */
const useMatrixAvatar = (
  matrixClient: MatrixClient | MutableRefObject<MatrixClient | null>,
  setMxAvatarUrl: SetState<string>
) => {
  const { t } = useTranslation();
  const { mxUserId } = useMxCookies();
  const { pushToast } = useToast();
  const [loading, setLoading] = useState(false);

  const getMatrixClient = useCallback(
    () => (matrixClient && 'current' in matrixClient ? matrixClient.current : matrixClient),
    [matrixClient]
  );

  const fetchAvatar = useCallback(
    async (avatarUrl?: string) => {
      const mxClient = getMatrixClient();

      if (!avatarUrl || !mxClient) return setMxAvatarUrl('');

      // Use shared utility to fetch media as blob URL
      const blobUrl = await fetchMediaAsBlobUrl(mxClient, avatarUrl, {
        width: 100,
        height: 100,
        method: 'crop',
      });

      setMxAvatarUrl(blobUrl || '');
    },
    [getMatrixClient, setMxAvatarUrl]
  );

  const changeAvatar = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const mxClient = getMatrixClient();

    try {
      setLoading(true);
      const file = target.files?.[0];
      if (!file || !mxClient) return;

      // Use shared utility to upload media to Matrix
      const mxcUri = await uploadMediaToMatrix(mxClient, file, { type: file.type });

      // Set avatar URL in Matrix profile
      await mxClient.setAvatarUrl(mxcUri);

      // Create blob URL for immediate display
      const arrayBuffer = await file.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: file.type });
      setMxAvatarUrl(URL.createObjectURL(blob));
    } catch (error) {
      logger.error('Error uploading avatar:', { error, mxUserId });
      pushToast({ title: t('error_uploading_avatar'), variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const clearAvatar = async () => {
    const mxClient = getMatrixClient();

    setLoading(true);
    await mxClient?.setAvatarUrl('');
    setMxAvatarUrl('');
    setLoading(false);
  };

  return { fetchAvatar, changeAvatar, clearAvatar, loading };
};

export default useMatrixAvatar;
